// src/components/layout/Navbar.jsx
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Button from '../common/Button';
import ThemeToggle from '../common/ThemeToggle';
import { useTheme } from '../../context/ThemeContext';

// Import your logos
import lightLogo from '../../assets/images/brand/logo-light.png';
import darkLogo from '../../assets/images/brand/logo-dark.png';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { theme } = useTheme();
  const location = useLocation();

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Product', path: '/products' },
    { label: 'Services', path: '/services' },
    { label: 'About', path: '/about' },
    { label: 'Blog', path: '/blog' },
    { label: 'Our A.I.', path: '/our-ai' },
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const logoSrc = theme === 'dark' ? darkLogo : lightLogo;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        theme === 'dark'
          ? 'bg-black/80 backdrop-blur-md border-b border-white/10'
          : 'bg-white backdrop-blur-md border-b border-gray-200 shadow-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">

          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src={logoSrc}
              alt="Dorots Logo"
              className="h-16 w-20 md:h-[4.5rem] md:w-24 lg:h-20 lg:w-28 object-contain transition-all duration-300"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  relative text-sm font-medium transition-colors duration-200
                  ${
                    isActive(item.path)
                      ? 'text-blue-600'
                      : theme === 'dark'
                        ? 'text-gray-300 hover:text-blue-500'
                        : 'text-gray-600 hover:text-blue-700'
                  }
                  group
                `}
              >
                {item.label}

                <span
                  className={`
                    absolute -bottom-1 left-0 h-0.5
                    bg-blue-700 transition-all duration-300
                    ${
                      isActive(item.path)
                        ? 'w-full'
                        : 'w-0 group-hover:w-full'
                    }
                  `}
                />
              </Link>
            ))}

            <ThemeToggle />

            <Button
              variant="outline"
              size="sm"
              className={
                theme === 'dark'
                  ? 'text-white border-white/20 hover:border-blue-500 hover:bg-blue-500/10'
                  : 'text-blue-700 border-blue-700 hover:bg-blue-700/10'
              }
            >
              <Link to="/contact">Contact</Link>
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-4">
            <ThemeToggle />

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={
                theme === 'dark'
                  ? 'text-white hover:text-blue-500 transition-colors'
                  : 'text-gray-900 hover:text-blue-700 transition-colors'
              }
              aria-label="Toggle menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div
            className={`md:hidden py-4 border-t ${
              theme === 'dark'
                ? 'border-white/10'
                : 'border-gray-200'
            }`}
          >
            <div className="flex flex-col space-y-4">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`
                    relative text-sm font-medium py-2
                    transition-colors duration-200
                    ${
                      isActive(item.path)
                        ? 'text-blue-600'
                        : theme === 'dark'
                          ? 'text-gray-300 hover:text-blue-500'
                          : 'text-gray-600 hover:text-blue-700'
                    }
                    group
                  `}
                >
                  {item.label}

                  <span
                    className={`
                      absolute -bottom-0 left-0 h-0.5
                      bg-blue-700 transition-all duration-300
                      ${
                        isActive(item.path)
                          ? 'w-full'
                          : 'w-0 group-hover:w-full'
                      }
                    `}
                  />
                </Link>
              ))}

              <Button
                variant="outline"
                size="sm"
                className={
                  theme === 'dark'
                    ? 'text-white border-white/20 hover:border-blue-500 hover:bg-blue-500/10'
                    : 'text-blue-700 border-blue-700 hover:bg-blue-700/10'
                }
              >
                <Link
                  to="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Contact
                </Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;